import { useCallback } from 'react'

import { useKeyboardShortcut } from '~publish/hooks/useKeyboardShortcut'

import { useCommandBarContext } from '../context/CommandBarContext'
import type { OpenSource } from '../context/CommandBarContext'

/**
 * Options for the useCommandBar hook.
 */
interface UseCommandBarOptions {
  /** Disable the keyboard shortcut */
  disabled?: boolean
}

/**
 * Return value from the useCommandBar hook.
 */
interface UseCommandBarResult {
  /** Whether the command bar is open */
  open: boolean
  /** Source that triggered opening the command bar */
  openSource: OpenSource | null
  /** Open the command bar */
  openCommandBar: (source?: OpenSource, returnFocusTo?: HTMLElement) => void
  /** Close the command bar */
  closeCommandBar: () => void
  /** Toggle the command bar */
  toggleCommandBar: (source?: OpenSource) => void
  /** Set the open state directly */
  setOpen: (open: boolean) => void
  restoreFocusOnClose: (event: Event) => void
}

/**
 * Hook to control the command bar and register keyboard shortcuts.
 *
 * Registers Cmd+K (Mac) / Ctrl+K (Windows) to open/toggle the command bar.
 */
export function useCommandBar(
  options: UseCommandBarOptions = {},
): UseCommandBarResult {
  const { disabled = false } = options
  const {
    open,
    openSource,
    openCommandBar,
    closeCommandBar,
    toggleCommandBar,
    setOpen,
    restoreFocusOnClose,
  } = useCommandBarContext()

  const handleToggle = useCallback(() => {
    toggleCommandBar('keyboard_shortcut')
  }, [toggleCommandBar])

  // Register Cmd+K / Ctrl+K shortcut
  useKeyboardShortcut('mod+k', handleToggle, {
    disabled,
    preventDefault: true,
    ignoreInputFocus: false, // Allow opening from input fields
  })

  return {
    open,
    openSource,
    openCommandBar,
    closeCommandBar,
    toggleCommandBar,
    setOpen,
    restoreFocusOnClose,
  }
}
